import api from './api';

export interface EmailTemplateRef {
  id: number;
  name: string;
  subject: string;
  content?: string;
}

export interface CampaignGroup {
  id: number;
  name: string;
  description?: string;
  group_type: string;
  member_count: number;
  auto_criteria?: Record<string, unknown>;
}

export interface Campaign {
  id: number;
  name: string;
  description: string;
  campaign_type: 'email' | 'whatsapp' | 'both';
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed' | 'cancelled';
  /** Present on list serializer */
  group_name?: string;
  group?: CampaignGroup;
  email_template?: EmailTemplateRef | number | null;
  whatsapp_template?: {
    id: number;
    name: string;
    content?: string;
    meta_template_name?: string;
    language_code?: string;
    body_parameter_sources?: string[];
  } | number | null;
  scheduled_at?: string | null;
  sent_at?: string | null;
  total_recipients: number;
  successful_sends: number;
  failed_sends: number;
  send_celery_task_id?: string | null;
  created_at: string;
  updated_at: string;
  created_by?: number;
  created_by_name?: string;
  logs?: CampaignLog[];
}

export interface CampaignLog {
  id: number;
  campaign: number;
  reservation: number;
  guest_name: string;
  property_name?: string;
  guest_country?: string;
  guest_city?: string;
  message_type: 'email' | 'whatsapp';
  recipient: string;
  status: string;
  sent_at: string;
  error_message?: string;
}

export interface CampaignRecipient {
  reservation_id: number;
  guest_name?: string | null;
  property_name?: string | null;
  email?: string | null;
}

export interface CampaignAnalytics {
  campaign_id: number;
  name: string;
  status: string;
  total_logs: number;
  sent_count: number;
  failed_count: number;
  success_rate: number;
  property_breakdown: Record<string, number>;
  status_breakdown: Record<string, number>;
  location_breakdown: Record<string, number>;
}

export interface CampaignCreateData {
  name: string;
  description?: string;
  campaign_type: 'email' | 'whatsapp' | 'both';
  email_template?: number;
  email_template_id?: number;
  whatsapp_template?: number;
  whatsapp_template_id?: number;
  group_id?: number;
  group?: number;
  audience_criteria?: Record<string, unknown>;
  audience_group_name?: string;
  scheduled_at?: string;
}

export interface CampaignListParams {
  page?: number;
  page_size?: number;
  search?: string;
  status?: string;
  campaign_type?: string;
  group_id?: number;
  ordering?: string;
}

export interface SendCampaignData {
  send_immediately?: boolean;
  scheduled_datetime?: string | null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface ApiPaginated<T> {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: T[];
}

const campaignService = {
  getCampaigns: async (params?: Record<string, unknown>) => {
    const response = await api.get('/campaigns/', { params });
    return response.data;
  },

  getCampaign: async (id: number): Promise<Campaign> => {
    const response = await api.get(`/campaigns/${id}/`);
    return response.data;
  },

  createCampaign: async (data: CampaignCreateData) => {
    const response = await api.post('/campaigns/', data);
    return response.data;
  },

  updateCampaign: async (id: number, data: Partial<CampaignCreateData>) => {
    const response = await api.patch(`/campaigns/${id}/`, data);
    return response.data;
  },

  deleteCampaign: async (id: number) => {
    await api.delete(`/campaigns/${id}/`);
  },

  sendCampaign: async (id: number, data: SendCampaignData) => {
    const response = await api.post(`/campaigns/${id}/send_campaign/`, data);
    return response.data;
  },

  getCampaignLogs: async (
    campaignId: number,
    params?: Record<string, unknown>
  ): Promise<ApiPaginated<CampaignLog>> => {
    const response = await api.get(`/campaigns/${campaignId}/logs/`, { params });
    return response.data;
  },

  getCampaignAnalytics: async (id: number): Promise<{ status: string; data: CampaignAnalytics }> => {
    const response = await api.get(`/campaigns/${id}/analytics/`);
    return response.data;
  },

  getCampaignRecipients: async (
    id: number,
    params?: { page?: number; page_size?: number }
  ): Promise<ApiPaginated<CampaignRecipient>> => {
    const response = await api.get(`/campaigns/${id}/recipients/`, { params });
    return response.data;
  },

  updateCampaignRecipients: async (
    id: number,
    recipients: Array<{ reservation_id: number; email?: string | null }>
  ): Promise<{ status: string; message?: string; updated_count: number }> => {
    const response = await api.patch(`/campaigns/${id}/recipients/`, { recipients });
    return response.data;
  },

  sendTestEmail: async (id: number, email: string): Promise<{ status: string; message?: string }> => {
    const response = await api.post(`/campaigns/${id}/send_test_email/`, { email });
    return response.data;
  },

  sendTestWhatsApp: async (id: number, phone: string): Promise<{ status: string; message?: string }> => {
    const response = await api.post(`/campaigns/${id}/send_test_whatsapp/`, { phone });
    return response.data;
  },

  getCeleryTaskStatus: async (id: number) => {
    const response = await api.get(`/campaigns/${id}/celery_task_status/`);
    return response.data;
  },
};

export default campaignService;
